import type Backbone from 'backbone';
import _ from 'underscore';
import groupProductsByCriterias from '@/js/common/Domain/Service/groupProductsByCriterias.js';
import { OperatorId } from '@entities/operator';
import { HotelAvailable } from '@entities/tourProduct/types/IHotelAvailable';
import { operators } from '@fr/constants';
import { type Operator } from '@ve/types';
import { type IBackboneTourProduct } from '@ve/types/backboneModels/Domain/Entity/BackboneTourProduct';
import { type FlightType } from '@ve/types/FlightType';

const SPLIT_BY_FLIGHT_TYPE_OPERATOR_IDS = new Set<number>([
  OperatorId.AnexTour,
  OperatorId.FunSun,
  OperatorId.NTK,
  OperatorId.SpaceTravel,
  OperatorId.RussianExpress,
  OperatorId.Paks,
  OperatorId.OneTouchTravel,
  OperatorId.LetsFly,
  OperatorId.Loti,
  OperatorId.ChinaTravel,
]);

const FLIGHT_TYPE_CHARTER: FlightType = 1;
const FLIGHT_TYPE_REGULAR: FlightType = 2;

interface TourFlightInfo {
  id: string;
  flightType?: FlightType;
}

/**
 * Для оператора-самософта: если самый дешёвый — чартер, отдаём только его;
 * если GDS — отдаём пару GDS+чартер. Для остальных операторов — только самый дешёвый.
 * Принимает массив, уже отсортированный по цене (от дешёвого к дорогому).
 */
const selectIdentitiesForOperator = <T extends TourFlightInfo>(
  sortedTours: T[],
  operatorId: number,
): T[] => {
  if (SPLIT_BY_FLIGHT_TYPE_OPERATOR_IDS.has(operatorId)) {
    const cheapestTour = sortedTours[0];
    const isCheapestTourCharter = cheapestTour?.flightType === FLIGHT_TYPE_CHARTER;
    if (isCheapestTourCharter) {
      return [cheapestTour];
    }
    const cheapestRegular = sortedTours.find((t) => t.flightType === FLIGHT_TYPE_REGULAR);
    const cheapestCharter = sortedTours.find((t) => t.flightType === FLIGHT_TYPE_CHARTER);
    return [cheapestRegular, cheapestCharter].filter((t): t is T => !!t);
  }
  return [sortedTours[0]];
};

const getCheapestByOperator = (tours: Backbone.Model[], product: Backbone.Model): Backbone.Model[] =>
  _.chain(tours)
    .groupBy((tourData) => (tourData.get('product') || product).get('operator').id)
    .pairs()
    .map(([operatorId, operatorTours]) => {
      const sortedTours = _.sortBy(operatorTours, (operatorTour) => operatorTour.get('finalPrice'));
      const toursFlightInfo = sortedTours.map((t) => ({
        id: t.get('id') as string,
        flightType: t.get('tour')?.get('flightType') as FlightType | undefined,
      }));
      const selectedIds = selectIdentitiesForOperator(toursFlightInfo, Number(operatorId)).map((t) => t.id);
      return sortedTours.filter((t) => selectedIds.includes(t.get('id')));
    })
    .flatten()
    .value();

const prepareTourIdentities = (tours: Backbone.Model[], product: Backbone.Model): Backbone.Model[] =>
  getCheapestByOperator(tours, product);

const prepareTourProductsWithoutHotelAndRailway = (
  tourProductsGroup: Backbone.Model[],
  product: Backbone.Model,
): Backbone.Model[] => {
  const tourProducts = tourProductsGroup.filter((tourData) => tourData.get('type') === 'TourProduct');
  return getCheapestByOperator(tourProducts, product);
};

const getCheapestTravelataTourWithTransfer = (
  tourProductsGroup: IBackboneTourProduct[],
): IBackboneTourProduct | null => {
  const travelataTours = tourProductsGroup.filter((tourProduct) => {
    const operator = tourProduct.get('tour')?.get('operator') as Operator;
    return operators.MY_AGENT.includes(operator?.id);
  });
  const travelataToursWithTransfer = travelataTours.filter((tourProduct) => tourProduct.get('transferType') !== 'none');
  const isTravelataToursWithTransferExists = travelataToursWithTransfer.length > 0;
  return isTravelataToursWithTransferExists
    ? _.sortBy(travelataToursWithTransfer, (tour) => tour.get('price'))[0]
    : null;
};

interface TravelataTourInfo {
  id: string;
  operatorId: number;
  price: number;
  transferType?: string;
  block?: boolean;
  hotelAvailable?: number;
}

const selectAdditionalTravelataTourIds = <T extends TravelataTourInfo>(
  tours: T[],
  cheapestToursIdentities: string[],
): string[] => {
  const travelataTours = tours.filter((t) => operators.MY_AGENT.includes(t.operatorId));
  const cheapestTravelataTour = travelataTours.find((t) => cheapestToursIdentities.includes(t.id));
  const sorted = _.sortBy(travelataTours, (t) => t.price);

  const tourRegular = sorted.find((t) => t.transferType === 'none' && !t.block && t.hotelAvailable === HotelAvailable.all);
  const tourRegularTransfer = sorted.find((t) => t.transferType !== 'none' && !t.block && t.hotelAvailable === HotelAvailable.all);
  const tourBlock = sorted.find((t) => t.transferType === 'none' && t.block);
  const tourBlockTransfer = sorted.find((t) => t.transferType !== 'none' && t.block);
  const tourOnRequest = sorted.find((t) => t.transferType === 'none' && !t.block && t.hotelAvailable === HotelAvailable.byRequest);
  const tourOnRequestTransfer = sorted.find((t) => t.transferType !== 'none' && !t.block && t.hotelAvailable === HotelAvailable.byRequest);

  return [tourRegular, tourRegularTransfer, tourBlock, tourBlockTransfer, tourOnRequest, tourOnRequestTransfer]
    .filter((t): t is T => !!t && t.id !== cheapestTravelataTour?.id)
    .map((t) => t.id);
};

const getAdditionalTravelataTours = (
  tourProductsGroup: IBackboneTourProduct[],
  cheapestToursIdentities: string[],
): IBackboneTourProduct[] => {
  const toursInfo = tourProductsGroup.map((t) => ({
    id: t.get('id') as string,
    operatorId: (t.get('tour')?.get('operator') as Operator)?.id,
    price: t.get('finalPrice') as number,
    transferType: t.get('transferType') as string,
    block: t.get('tour')?.get('block') as boolean | undefined,
    hotelAvailable: t.get('tour')?.get('hotelAvailable') as number | undefined,
  }));
  const selectedIds = selectAdditionalTravelataTourIds(toursInfo, cheapestToursIdentities);
  return tourProductsGroup.filter((t) => selectedIds.includes(t.get('id') as string));
};

const getAdditionalToursForBonusMiles = (
  tourProducts: IBackboneTourProduct[],
  allProducts: IBackboneTourProduct[],
): IBackboneTourProduct[] => {
  if (tourProducts.length === 0 || allProducts.length === 0) {
    return [];
  }
  const groupedProducts = groupProductsByCriterias(allProducts);
  const selectedTourId = tourProducts[0]?.get('id');

  const selectedTourGroup = groupedProducts.find((group: Backbone.Model[]) =>
    group.some((tour) => tour.get('id') === selectedTourId),
  );

  if (!selectedTourGroup) {
    return [];
  }
  const existingIds = new Set(tourProducts.map((t) => t.get('id')));
  return selectedTourGroup.filter((tour: Backbone.Model) => !existingIds.has(tour.get('id')));
};

export {
  selectIdentitiesForOperator,
  selectAdditionalTravelataTourIds,
  prepareTourProductsWithoutHotelAndRailway,
  prepareTourIdentities,
  getCheapestTravelataTourWithTransfer,
  getAdditionalTravelataTours,
  getAdditionalToursForBonusMiles,
};
